import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { subtasksApi } from "@/features/tasks/api/subtasks";
import { taskKeys } from "@/features/tasks/hooks/useTasks";
import type { SubTask, SubTaskFilters } from "@/features/tasks/types";

export function useEmployeeStats() {
  return useQuery({
    queryKey: ["employee-task-stats"],
    queryFn: () => subtasksApi.employeeStats(),
    staleTime: 1000 * 60 * 2,
  });
}

export function useEmployeeSubTasks(userId: string) {
  return useQuery({
    queryKey: ["subtasks", "all", { assigned_to: [userId] }],
    queryFn: () => subtasksApi.listAll({ assigned_to: [userId] }),
    enabled: !!userId,
  });
}

export const subKeys = {
  list: (taskId: string) => ["subtasks", taskId] as const,
  all: (filters?: SubTaskFilters) => ["subtasks", "all", filters] as const,
  my: () => ["subtasks", "my"] as const,
};

export function useSubTasks(taskId: string) {
  return useQuery({
    queryKey: subKeys.list(taskId),
    queryFn: () => subtasksApi.list(taskId),
    enabled: !!taskId,
  });
}

export function useAllSubTasks(filters?: SubTaskFilters) {
  return useQuery({
    queryKey: subKeys.all(filters),
    queryFn: () => subtasksApi.listAll(filters),
    placeholderData: keepPreviousData,
  });
}

export function useMySubTasks(enabled = true) {
  return useQuery({
    queryKey: subKeys.my(),
    queryFn: () => subtasksApi.my(),
    enabled,
  });
}

export function useCreateSubTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; parent?: string | null; due_date?: string | null; assignee_ids?: string[]; priority?: string; status?: string }) =>
      subtasksApi.create(taskId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: ["subtasks", "all"] });
      qc.invalidateQueries({ queryKey: subKeys.my() });
    },
  });
}

export function useUpdateSubTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SubTask> & { assignee_ids?: string[] } }) =>
      subtasksApi.update(taskId, id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      qc.invalidateQueries({ queryKey: ["subtasks", "all"] });
      qc.invalidateQueries({ queryKey: subKeys.my() });
    },
  });
}

export function useDeleteSubTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subtasksApi.delete(taskId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: ["subtasks", "all"] });
      qc.invalidateQueries({ queryKey: subKeys.my() });
    },
  });
}

export function useReorderSubTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ids }: { taskId: string; ids: string[] }) =>
      subtasksApi.reorder(taskId, ids),
    onMutate: async ({ taskId, ids }) => {
      // Cancel outgoing refetches to prevent race conditions
      await qc.cancelQueries({ queryKey: subKeys.list(taskId) });

      // Snapshot per-task cache for rollback
      const prev = qc.getQueryData<SubTask[]>(subKeys.list(taskId));

      // Optimistically reorder the per-task cache (used by SubTaskList detail panel)
      if (prev) {
        const posMap = new Map(ids.map((id, i) => [id, i]));
        qc.setQueryData<SubTask[]>(subKeys.list(taskId), (old) => {
          if (!old) return old;
          return [...old]
            .map((st) => ({ ...st, position: posMap.get(st.id) ?? st.position }))
            .sort((a, b) => a.position - b.position);
        });
      }

      return { prev };
    },
    onError: (_err, { taskId }, context) => {
      if (context?.prev) qc.setQueryData(subKeys.list(taskId), context.prev);
    },
    onSuccess: (sortedSubtasks, { taskId }) => {
      // Backend returned the newly-ordered subtasks — set per-task cache directly
      qc.setQueryData<SubTask[]>(subKeys.list(taskId), sortedSubtasks);

      // Update all global ["subtasks", "all", *] caches with the new order.
      // Enumerate exact keys first, then setQueryData with exact key for reliability.
      const globalEntries = qc.getQueriesData<SubTask[]>({ queryKey: ["subtasks", "all"] });
      for (const [key] of globalEntries) {
        qc.setQueryData<SubTask[]>(key, (old) => {
          if (!old) return old;
          const others = old.filter((st) => st.task !== taskId);
          return [...others, ...sortedSubtasks];
        });
      }

      qc.invalidateQueries({ queryKey: subKeys.my() });
    },
    onSettled: (_, _err, { taskId }) => {
      // Force a background refetch to sync with server state
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      qc.invalidateQueries({ queryKey: ["subtasks", "all"] });
    },
  });
}

export function useMoveSubTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, subtaskId, newTaskId }: { taskId: string; subtaskId: string; newTaskId: string }) =>
      subtasksApi.move(taskId, subtaskId, newTaskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtasks"] });
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useReparentSubTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, subtaskId, parent_id, task_id }: { taskId: string; subtaskId: string; parent_id?: string | null; task_id?: string }) =>
      subtasksApi.reparent(taskId, subtaskId, { parent_id, task_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtasks"] });
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useToggleSubTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => subtasksApi.toggle(taskId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: ["subtasks", "all"] });
      qc.invalidateQueries({ queryKey: subKeys.my() });
    },
  });
}

export function useSubTaskAttachments(taskId: string, subtaskId: string) {
  return useQuery({
    queryKey: ["subtask-attachments", taskId, subtaskId],
    queryFn: () => subtasksApi.listAttachments(taskId, subtaskId),
    enabled: !!taskId && !!subtaskId,
  });
}

export function useUploadSubTaskAttachment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => subtasksApi.uploadAttachment(taskId, subtaskId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtask-attachments", taskId, subtaskId] });
    },
  });
}

export function useDeleteSubTaskAttachment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => subtasksApi.deleteAttachment(taskId, subtaskId, attachmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtask-attachments", taskId, subtaskId] });
    },
  });
}

export function useUploadCommentAttachment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, file }: { commentId: string; file: File }) =>
      subtasksApi.uploadCommentAttachment(taskId, subtaskId, commentId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtask-comments", subtaskId] });
    },
  });
}

export function useWatchSubTask(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (watching: boolean) =>
      watching ? subtasksApi.watch(taskId, subtaskId) : subtasksApi.unwatch(taskId, subtaskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subKeys.list(taskId) });
      qc.invalidateQueries({ queryKey: ["subtasks", "all"] });
    },
  });
}

export function useDeleteCommentAttachment(taskId: string, subtaskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, attachmentId }: { commentId: string; attachmentId: string }) =>
      subtasksApi.deleteCommentAttachment(taskId, subtaskId, commentId, attachmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtask-comments", subtaskId] });
    },
  });
}
